import {
  useState,
  useCallback,
  ChangeEvent,
  useEffect,
  useMemo,
  FC,
} from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Button, Loader } from "@storybook";

import { Face, useNetwork } from "hooks";
import { LoginPhoneNumberState, SignInActiveStep } from "states";
import { API_URL, REGEX_ACCEPT_ONLY_NUMBER } from "constant";

import styles from "./Form.module.sass";

export interface IOtpObject {
  otp1: string;
  otp2: string;
  otp3: string;
  otp4: string;
}

interface IOtpVerify {
  onSuccess: Function;
  onFailed: Function;
  locationInfo: any;
  ipAddress: string;
}
export const OtpVerify: FC<IOtpVerify> = ({
  onSuccess,
  onFailed,
  locationInfo,
  ipAddress,
}) => {
  const loginPhoneNumber = useRecoilValue(LoginPhoneNumberState);
  const [otpValue, setOtpValue] = useState<IOtpObject>({
    otp1: "",
    otp2: "",
    otp3: "",
    otp4: "",
  });
  const [phoneNumber] = useState<string>(loginPhoneNumber.phone);
  const [countryCode] = useState<string | number>(loginPhoneNumber.countryCode);
  const setSignInActiveStep = useSetRecoilState(SignInActiveStep);

  const {
    post: verifyPhone,
    data: verifyOTP,
    loading: verifyOtpLoading,
  } = useNetwork();

  const handleSubmitOtp = useCallback(() => {
    const { otp1, otp2, otp3, otp4 } = otpValue;
    const sentOtp = otp1 + otp2 + otp3 + otp4;
    verifyPhone(`${API_URL.VerifyPhones}`, {
      phone: phoneNumber,
      otp: sentOtp,
      countryCode,
    });
  }, [verifyPhone, countryCode, otpValue, phoneNumber]);

  const handleBack = useCallback(() => {
    setSignInActiveStep("chooseMethod");
  }, [setSignInActiveStep]);

  const handleGoBack = useCallback(() => {
    Face.apiCalling= true
    setSignInActiveStep("phone");
  }, [setSignInActiveStep]);

  useEffect(() => {
    if (verifyOTP) {
      const { token } = verifyOTP;
      if (token) {
        const payload = {
          success: true,
          location: locationInfo,
          ip: ipAddress,
        };
        onSuccess(payload);
      } else {
        onFailed(verifyOTP);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, verifyOTP]);

  const onHandleOtp = useCallback(
    (name: string, e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      const { value } = e.target;
      setOtpValue((prev: IOtpObject) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const inputFocus = useCallback((event: any) => {
    if (
      event.target.value === "" ||
      REGEX_ACCEPT_ONLY_NUMBER.test(event.target.value)
    ) {
      if (event.key === "Delete" || event.key === "Backspace") {
        const next = event.target.tabIndex - 2;
        if (next > -1) {
          event.target.form.elements[next].focus();
        }
      } else {
        const next = event.target.tabIndex;
        if (next < 4) {
          event.target.form.elements[next].focus();
        }
      }
    } else {
      return;
    }
  }, []);

  const otpInput = useMemo(() => {
    return Object.entries(otpValue).map(([key, value], index) => (
      <input
        key={index}
        className={styles.otpInput}
        name={key}
        value={value}
        onChange={(e) => onHandleOtp(key, e)}
        onKeyUp={(e) => inputFocus(e)}
        maxLength={1}
        minLength={1}
        tabIndex={index + 1}
        onFocus={(e) => e.target.select}
        autoFocus={index === 0}
      />
    ));
  }, [inputFocus, onHandleOtp, otpValue]);

  const { otp1, otp2, otp3, otp4 } = otpValue;
  const submitBtn = !(otp1 && otp2 && otp3 && otp4);

  return (
    // otp verify screen
    <div className={styles.wrapper}>
      <form className={styles.form}>
        <div className={styles.otp}>
          <div className={styles.inner}>
            <div className={styles.title}>Enter The Verification Code</div>
            <div className={styles.sub_title}>
              Enter the OTP sent to : {countryCode} {phoneNumber}
              <span className={styles.editBtn} onClick={handleGoBack}>
                {" "}
                Edit
              </span>
            </div>
            <div className={styles.container}>
              <div className={styles.line}>{otpInput}</div>
            </div>
            <div>
              <div className={styles.verify_button}>
                <Button
                  type="button__filled--primary button__large button__block"
                  handleClick={handleSubmitOtp}
                  label={verifyOtpLoading ? "" : "Verify"}
                  loader={verifyOtpLoading && <Loader dimension={18} />}
                  disabled={submitBtn}
                  buttonType="button"
                />
              </div>
              <div className={styles.verify_button}>
                <Button
                  type={"button__filled--secondary button__large button__block"}
                  handleClick={handleBack}
                  label="Back"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
